import { Injectable } from '@angular/core';
import MainScene from '../Scenes/MainScene';
import { PlayerMetadata } from 'src/classes/PlayerMetadata';
import { actions } from '../../../infrastructure/lib/src/models/consts';
import { environment } from '../../../environment';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';

import { Peer } from 'src/classes/Peer';
import { GlobalService, ModeState } from './global.service';
import { showToast } from 'src/classes/Utilities';
import BaseLevelScene from '../Scenes/BaseLevelScene';
import { SlingshotEvent } from 'infrastructure/lib/src/models/slingshot-event';
import { DropItemEvent } from 'infrastructure/lib/src/models/drop-item-event';
import { DropItem } from 'src/classes/DropItem';

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  static socket: WebSocket;
  static scene: MainScene | BaseLevelScene;
  static authenticator: AuthenticatorService = new AuthenticatorService();
  static session: AuthSession;
  static playerUpdateTimestamps: Map<string, number> = new Map();
  isConnecting: boolean = false;
  isConnected: boolean = false;
  retryCount: number = 0;
  maxRetries: number = 5;

  static setScene(scene: MainScene | BaseLevelScene): void {
    if (WebsocketService.scene) {
      WebsocketService.scene.clearPeers();
    }
    WebsocketService.scene = scene;
  }

  async connect(): Promise<void> {

    if (this.isConnecting || this.isConnected) {
      console.log('WebSocket connection already in progress or established.');
      return;
    }
    this.isConnecting = true;
    this.retryCount = 0;
    await this.tryConnect();
  }

  async tryConnect() {
    try{
      WebsocketService.session = await fetchAuthSession();
      const token = WebsocketService.session.tokens.idToken;
      WebsocketService.socket = new WebSocket(`${environment.WSS_ENDPOINT}?Auth=${token}`);
    } catch (error){
      WebsocketService.session = null;
      WebsocketService.socket = new WebSocket(`${environment.WSS_ENDPOINT}`);
    }

    WebsocketService.socket.onopen = async () => {
      this.isConnecting = false;
      this.isConnected = true;
      this.retryCount = 0;
      console.log('WebSocket connection established.');
      GlobalService.$onlineModeState.next(ModeState.On);
      await showToast({ message:"Successfully connected to websockets!", duration: 1000});
      // if(WebsocketService.authenticator.username){
      //   WebsocketService.scene.playerMetadata.playerName = WebsocketService.authenticator.username;
      // }
      WebsocketService.scene.playerMetadata.lastUpdated = Date.now();
      WebsocketService.scene.playerMetadata.levelName = WebsocketService.scene.scene.key;
      WebsocketService.updatePlayer(WebsocketService.scene.playerMetadata);
    };

    WebsocketService.socket.onmessage = (event) => {
      // NOTE: This manual routing feels so dirty and wrong.
      const data = JSON.parse(event.data);
      const action = data.action;
      switch(action){
        case actions.ALL_PLAYERS:
          const allPlayers = data.players as Array<PlayerMetadata>;
          WebsocketService.addAllPlayersToScene(allPlayers);
          break;
        case actions.PLAYER_ADDED:
          const newPlayer = data.player as PlayerMetadata;
          WebsocketService.addAnotherPlayer(newPlayer);
          break;
        case actions.PLAYER_UPDATED:
          const updatedPlayer = data.playerData as PlayerMetadata;
          WebsocketService.updateOtherPlayersPosition(updatedPlayer);
          break;
        case actions.PLAYER_DELETED:
          const deletedPlayerConnectionId: string = data.connectionId;
          WebsocketService.removeSinglePlayer(deletedPlayerConnectionId, data.timestamp);
          WebsocketService.scene.player.peersInRange = WebsocketService.scene.player.peersInRange.filter(peer => peer.name !== deletedPlayerConnectionId);
          break;
        case actions.SLINGSHOT_FIRED:
          WebsocketService.fireSlingshot(data.slingstone as SlingshotEvent);
          break;
        case actions.ITEM_DROPPED:
          console.log("Received ITEM_DROPPED event:", data);
          WebsocketService.handleItemDropped(data.dropItemEvent as DropItemEvent);
          break;
        default:
          console.warn(`Unhandled action type: ${action}`);
      }
    };

    WebsocketService.socket.onclose = (event) => {
      this.isConnected = false;
      this.isConnecting = false;
      WebsocketService.cleanup(event);
    };

    WebsocketService.socket.onerror = async (error) => {
      this.isConnected = false;
      this.isConnecting = false;
      WebsocketService.cleanup(error);
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.error(`WebSocket error: retrying (${this.retryCount}/${this.maxRetries})...`);
        setTimeout(() => this.tryConnect(), 2000);
      } else {
        await showToast({message: "Unable to connect to websockets at this time.", duration: 3000});
      }
    };
  }


  public static initReconnectListeners(serviceInstance: WebsocketService) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !serviceInstance.isConnected && !serviceInstance.isConnecting) {
        serviceInstance.connect();
      }
    });
    window.addEventListener('online', () => {
      if (!serviceInstance.isConnected && !serviceInstance.isConnecting) {
        serviceInstance.connect();
      }
    });
  }

  static cleanup(event: CloseEvent | Event){
      console.log('WebSocket connection closed:', event);
      WebsocketService.removeAllPlayers();
      console.log("Closing websockets!");
      while (WebsocketService.scene.otherPlayers.getChildren().length > 0) {
        (WebsocketService.scene.otherPlayers.getChildren() as Peer[]).forEach(
          (peer) => peer.customDestroy()
        );
      }
      GlobalService.$onlineModeState.next(ModeState.Off);
  }

  // Calls to Lambda
  static getAllPlayers(){
    const formattedPayload: string = JSON.stringify({action: "getAllPlayers"});
    console.log(`WebSockets calling getAllPlayers: ${formattedPayload}`);
    WebsocketService.socket.send(formattedPayload);
  }

  static sendMessage(message: string): void {
    if(WebsocketService.socket && WebsocketService.socket.readyState === WebsocketService.socket.OPEN){
      WebsocketService.socket.send(message);
      console.log('Message sent')
    }
  }

  static updatePlayer(playerData: PlayerMetadata){
    if(WebsocketService.socket && WebsocketService.socket.readyState === WebsocketService.socket.OPEN){
      const formattedPayload: string = JSON.stringify({action: "updatePlayer", playerData: playerData});
      WebsocketService.socket.send(formattedPayload);
    }
  }

  static broadcastSlingshotEvent(shotEventData: SlingshotEvent): void {
    if(WebsocketService.socket && WebsocketService.socket.readyState === WebsocketService.socket.OPEN){
      const formattedPayload: string = JSON.stringify({action: "slingshotFired", eventData: shotEventData});
      console.log(`WebSocket broadcasting slingshot event: ${formattedPayload}`);
      WebsocketService.socket.send(formattedPayload);
    }
  }

  static broadcastDropItemEvent(dropEventData: DropItemEvent): void {
    console.log("broadcastDropItemEvent called");
    console.log("itemName:", dropEventData.itemName, "x:", dropEventData.x, "y:", dropEventData.y, "levelName:", dropEventData.levelName);
    if(WebsocketService.socket && WebsocketService.socket.readyState === WebsocketService.socket.OPEN){
      const formattedPayload: string = JSON.stringify({action: "itemDropped", eventData: dropEventData});
      console.log(`WebSocket broadcasting item drop event: ${formattedPayload}`);
      WebsocketService.socket.send(formattedPayload);
    }
  }

  static handleItemDropped(eventData: DropItemEvent): void {
    console.log("handleItemDropped called with eventData:", eventData);
    if(WebsocketService.scene){
      new DropItem({ scene: WebsocketService.scene, x: eventData.x, y: eventData.y, frame: eventData.itemName }, WebsocketService.scene.playerScale);
    } else {
      console.warn("Current scene is not a BaseLevelScene. Cannot spawn dropped item.");
    }
  }

  closeConnection(): void {
    if (WebsocketService.socket) {
      WebsocketService.socket.close();
      this.isConnected = false;
      this.isConnecting = false;
    }
  }

  static fireSlingshot(slingstone: SlingshotEvent){
    this.scene.player.bulletGroup.fireSlingshot(slingstone.x, slingstone.y, slingstone.velocityX, slingstone.velocityY, slingstone.scale, slingstone.connectionId);
  }

  // Updates to game based on Lambda data
  static addAllPlayersToScene(allPlayers: Array<PlayerMetadata>): void{
    allPlayers.forEach((player) => {
      WebsocketService.addAnotherPlayer(player);
      WebsocketService.playerUpdateTimestamps.set(player.connectionId, player.lastUpdated);
    });
  }

  static updateOtherPlayersPosition(playerInfo: PlayerMetadata){
    const lastUpdated = WebsocketService.playerUpdateTimestamps.get(playerInfo.connectionId);
    const sprites = WebsocketService.scene.otherPlayers.getChildren() as Peer[];
    let playerFound = false;
    for(let i=0; i<sprites.length; i++){
      const otherPlayer = sprites[i];
      if (playerInfo.connectionId === otherPlayer.name) {
        if(!otherPlayer.lastUpdated || otherPlayer.lastUpdated < playerInfo.lastUpdated){
          otherPlayer.lastUpdated = playerInfo.lastUpdated;
          otherPlayer.updatePosition(playerInfo);
          WebsocketService.playerUpdateTimestamps.set(playerInfo.connectionId, playerInfo.lastUpdated);
        }
        playerFound = true;
      }
    }
    // If the updated player was not found, add it—even if the timestamp is not newer
    if ((!playerFound && !lastUpdated) || (!playerFound && lastUpdated && playerInfo.lastUpdated >= lastUpdated)) {
      WebsocketService.addAnotherPlayer(playerInfo);
    } else if (lastUpdated && playerInfo.lastUpdated <= lastUpdated) {
      return;
    }
  }

  // Relevant helper functions
  static addAnotherPlayer(playerInfo: PlayerMetadata) {
    console.log("Adding another player:", playerInfo);
    WebsocketService.playerUpdateTimestamps.set(playerInfo.connectionId, playerInfo.lastUpdated);
    new Peer( playerInfo, WebsocketService.scene, 'player');
  }

  static removeSinglePlayer(connectionId: string, timestamp: number){
    const group = WebsocketService.scene.otherPlayers;
    (group.getChildren() as Peer[]).forEach((otherPlayer) => {
      if (connectionId === otherPlayer.name) {
        console.log(`playerDisconnected: ${connectionId}`);
        otherPlayer.customDestroy();
        otherPlayer.lastUpdated = timestamp;
        WebsocketService.playerUpdateTimestamps.set(connectionId, otherPlayer.lastUpdated);
        // group.remove(otherPlayer, true, true); // Remove from group and destroy
      }
    });
  }

  static removeAllPlayers(){
    const group = WebsocketService.scene.otherPlayers;
    console.log('destroying all other players');
    (group.getChildren() as  Peer[]).forEach((otherPlayer) => {
      otherPlayer.customDestroy();
    });
  }
}